import type { FFmpeg } from "@ffmpeg/ffmpeg";

// FFmpeg service using 0.11.x API for Next.js compatibility
// Types for conversion options
export interface ConversionOptions {
	format: string;
	videoCodec?: string;
	audioCodec?: string;
	quality?: string;
	resolution?: string;
	frameRate?: string;
	keepAudio?: boolean;
	bitrate?: string;
	sampleRate?: string;
	channels?: string;
	gifSize?: string;
	gifFrameRate?: string;
	loop?: string;
	startTime?: number;
	duration?: number;
}

export interface ConversionProgress {
	time: number;
	percent: number;
}

export interface CompressOptions {
	goal: "quality" | "balanced" | "smaller" | "target";
	targetBytes?: number;
	resolution: "original" | "1080p" | "720p" | "480p";
	keepAudio: boolean;
	durationSeconds: number;
}

export interface CompressProgress {
	percent: number;
	pass: 1 | 2;
}

let ffmpegInstance: FFmpeg | null = null;
let isLoading = false;

export async function loadFFmpeg(): Promise<void> {
	if (ffmpegInstance) return;
	if (isLoading) {
		await new Promise((resolve) => setTimeout(resolve, 100));
		return loadFFmpeg();
	}

	isLoading = true;

	try {
		const { createFFmpeg } = await import("@ffmpeg/ffmpeg/dist/ffmpeg.min.js");
		const assetBaseUrl = new URL("/ffmpeg/", window.location.origin);

		ffmpegInstance = createFFmpeg({
			log: true,
			corePath: new URL("ffmpeg-core.js", assetBaseUrl).href,
			wasmPath: new URL("ffmpeg-core.wasm", assetBaseUrl).href,
			workerPath: new URL("ffmpeg-core.worker.js", assetBaseUrl).href,
		});

		// Load FFmpeg
		await ffmpegInstance.load();

		isLoading = false;
	} catch (error) {
		isLoading = false;
		throw new Error(
			`Failed to load FFmpeg: ${error instanceof Error ? error.message : String(error)}`,
		);
	}
}

// Helper function to convert file to Uint8Array
async function fileToUint8Array(file: File): Promise<Uint8Array> {
	return new Uint8Array(await file.arrayBuffer());
}

function copyToArrayBuffer(data: Uint8Array): ArrayBuffer {
	const buffer = new ArrayBuffer(data.byteLength);
	new Uint8Array(buffer).set(data);
	return buffer;
}

export async function probeVideoDuration(file: File): Promise<number> {
	const ffmpeg = ffmpegInstance;
	if (!ffmpeg) {
		throw new Error("FFmpeg not loaded. Call loadFFmpeg() first.");
	}

	const inputName = `probe_${Date.now()}_${file.name}`;

	try {
		ffmpeg.FS("writeFile", inputName, await fileToUint8Array(file));

		await ffmpeg.run("-i", inputName, "-f", "null", "-");

		ffmpeg.FS("unlink", inputName);

		const logs = ffmpeg.FS("readFile", "ffmpeg.log");
		const logText = new TextDecoder().decode(logs);
		const durationMatch = logText.match(/Duration: (\d+):(\d+):(\d+\.\d+)/);

		if (durationMatch) {
			const hours = parseInt(durationMatch[1], 10);
			const minutes = parseInt(durationMatch[2], 10);
			const seconds = parseFloat(durationMatch[3]);
			const duration = hours * 3600 + minutes * 60 + seconds;

			if (duration > 0 && Number.isFinite(duration)) {
				return duration;
			}
		}

		throw new Error("could not read video duration");
	} catch (error) {
		ffmpeg.FS("unlink", inputName);
		throw new Error("could not read video duration");
	}
}

export async function compressVideo({
	file,
	options,
	onProgress,
	signal,
}: {
	file: File;
	options: CompressOptions;
	onProgress?: (progress: CompressProgress) => void;
	signal?: AbortSignal;
}): Promise<Blob> {
	const ffmpeg = ffmpegInstance;
	if (!ffmpeg) {
		throw new Error("FFmpeg not loaded. Call loadFFmpeg() first.");
	}

	const inputName = `input_${Date.now()}_${file.name}`;
	const outputName = `output_${Date.now()}.mp4`;

	try {
		ffmpeg.FS("writeFile", inputName, await fileToUint8Array(file));

		const args: string[] = [];
		args.push("-i", inputName);

		const { goal, resolution, keepAudio } = options;

		if (resolution !== "original") {
			const heightMap: Record<string, number> = {
				"1080p": 1080,
				"720p": 720,
				"480p": 480,
			};
			const height = heightMap[resolution] || 720;
			args.push("-vf", `scale=-2:${height}`);
		}

		const crfMap: Record<string, number> = {
			quality: 20,
			balanced: 26,
			smaller: 32,
		};

		if (goal === "target") {
			const duration = options.durationSeconds;
			const targetBytes = options.targetBytes ?? 0;
			const audioBitrateKbps = keepAudio ? 96 : 0;
			const videoBitrateKbps = Math.floor(
				((targetBytes * 8) / duration - audioBitrateKbps * 1000) * 0.95 / 1000
			);

			if (videoBitrateKbps <= 0) {
				throw new Error("target infeasible: target size is too small for the video duration");
			}

			args.push("-c:v", "libx264", "-b:v", `${videoBitrateKbps}k`, "-pass", "1", "-f", "null", "-");
		} else {
			const crf = crfMap[goal] ?? 26;
			const preset = goal === "smaller" ? "faster" : "medium";
			args.push("-c:v", "libx264", "-crf", crf.toString(), "-preset", preset);
		}

		if (keepAudio) {
			args.push("-c:a", "aac", "-b:a", "96k");
		} else {
			args.push("-an");
		}

		if (goal === "target") {
			const passlogName = `passlog_${Date.now()}`;
			args.push("-passlogfile", passlogName);

			ffmpeg.setProgress(({ ratio }) => {
				onProgress?.({ percent: Math.round(ratio * 50), pass: 1 });
			});

			await ffmpeg.run(...args);

			const pass2Args = args.filter(
				(arg, i) => !["-pass", "1", "-f", "null", "-"].includes(arg) && !args[i - 1]?.startsWith("-passlogfile")
			);
			const pass2Index = pass2Args.indexOf("-pass");
			if (pass2Index !== -1) {
				pass2Args[pass2Index + 1] = "2";
			}

			const outputIndex = pass2Args.indexOf("-f");
			if (outputIndex !== -1) {
				pass2Args.splice(outputIndex, 3);
			}
			pass2Args.push(outputName);

			ffmpeg.setProgress(({ ratio }) => {
				onProgress?.({ percent: Math.round(50 + ratio * 50), pass: 2 });
			});

			await ffmpeg.run(...pass2Args);
		} else {
			args.push(outputName);

			ffmpeg.setProgress(({ ratio }) => {
				onProgress?.({ percent: Math.round(ratio * 100), pass: 2 });
			});

			await ffmpeg.run(...args);
		}

		const data = ffmpeg.FS("readFile", outputName);

		ffmpeg.FS("unlink", inputName);
		ffmpeg.FS("unlink", outputName);

		return new Blob([copyToArrayBuffer(data)], { type: "video/mp4" });
	} catch (error) {
		ffmpeg.FS("unlink", inputName);
		try {
			ffmpeg.FS("unlink", outputName);
		} catch {}
		throw error;
	}
}

interface ConvertFileOptions {
	file: File;
	options: ConversionOptions;
	onProgress?: (progress: ConversionProgress) => void;
}

export async function convertVideo({
	file,
	options,
	onProgress,
}: ConvertFileOptions): Promise<Blob> {
	const ffmpeg = ffmpegInstance;
	if (!ffmpeg) {
		throw new Error("FFmpeg not loaded. Call loadFFmpeg() first.");
	}

	const inputName = `input_${Date.now()}_${file.name}`;
	const outputName = `output_${Date.now()}.${options.format.toLowerCase()}`;

	// Set progress callback
	ffmpeg.setProgress(({ ratio }) => {
		onProgress?.({ time: 0, percent: Math.round(ratio * 100) });
	});

	// Write input file
	ffmpeg.FS("writeFile", inputName, await fileToUint8Array(file));

	// Build FFmpeg command
	const args = buildFFmpegArgs({
		inputName,
		outputName,
		options,
		category: "video",
	});

	// Run FFmpeg
	await ffmpeg.run(...args);

	// Read output file
	const data = ffmpeg.FS("readFile", outputName);

	// Clean up
	ffmpeg.FS("unlink", inputName);
	ffmpeg.FS("unlink", outputName);

	return new Blob([copyToArrayBuffer(data)], {
		type: getMimeType(options.format),
	});
}

export async function convertAudio({
	file,
	options,
	onProgress,
}: ConvertFileOptions): Promise<Blob> {
	const ffmpeg = ffmpegInstance;
	if (!ffmpeg) {
		throw new Error("FFmpeg not loaded. Call loadFFmpeg() first.");
	}

	const inputName = `input_${Date.now()}_${file.name}`;
	const outputName = `output_${Date.now()}.${options.format.toLowerCase()}`;

	// Set progress callback
	ffmpeg.setProgress(({ ratio }) => {
		onProgress?.({ time: 0, percent: Math.round(ratio * 100) });
	});

	// Write input file
	ffmpeg.FS("writeFile", inputName, await fileToUint8Array(file));

	// Build FFmpeg command
	const args = buildFFmpegArgs({
		inputName,
		outputName,
		options,
		category: "audio",
	});

	// Run FFmpeg
	await ffmpeg.run(...args);

	// Read output file
	const data = ffmpeg.FS("readFile", outputName);

	// Clean up
	ffmpeg.FS("unlink", inputName);
	ffmpeg.FS("unlink", outputName);

	return new Blob([copyToArrayBuffer(data)], {
		type: getMimeType(options.format),
	});
}

export async function convertToGif({
	file,
	options,
	onProgress,
}: ConvertFileOptions): Promise<Blob> {
	const ffmpeg = ffmpegInstance;
	if (!ffmpeg) {
		throw new Error("FFmpeg not loaded. Call loadFFmpeg() first.");
	}

	const inputName = `input_${Date.now()}_${file.name}`;
	const outputName = `output_${Date.now()}.gif`;

	// Set progress callback
	ffmpeg.setProgress(({ ratio }) => {
		onProgress?.({ time: 0, percent: Math.round(ratio * 100) });
	});

	// Write input file
	ffmpeg.FS("writeFile", inputName, await fileToUint8Array(file));

	// Build FFmpeg command
	const args = buildFFmpegArgs({
		inputName,
		outputName,
		options,
		category: "image",
	});

	// Run FFmpeg
	await ffmpeg.run(...args);

	// Read output file
	const data = ffmpeg.FS("readFile", outputName);

	// Clean up
	ffmpeg.FS("unlink", inputName);
	ffmpeg.FS("unlink", outputName);

	return new Blob([copyToArrayBuffer(data)], { type: "image/gif" });
}

function buildFFmpegArgs({
	inputName,
	outputName,
	options,
	category,
}: {
	inputName: string;
	outputName: string;
	options: ConversionOptions;
	category: "video" | "audio" | "image";
}): string[] {
	const args: string[] = [];

	switch (category) {
		case "video": {
			// Select appropriate video codec based on output format
			const formatLower = options.format.toLowerCase();
			let defaultVideoCodec = "libx264";
			let defaultAudioCodec = "aac";

			// WebM format requires VP9/VP8 video and Opus/Vorbis audio
			if (formatLower === "webm") {
				defaultVideoCodec = "libvpx-vp9";
				defaultAudioCodec = "libopus";
			}
			// MKV format can use H.264 but let's ensure compatibility
			else if (formatLower === "mkv") {
				defaultVideoCodec = "libx264";
				defaultAudioCodec = "aac";
			}

			args.push("-i", inputName);
			args.push("-c:v", options.videoCodec || defaultVideoCodec);
			args.push(
				"-preset",
				options.quality === "Smaller file"
					? "faster"
					: options.quality === "Best"
						? "slower"
						: "medium",
			);

			if (options.resolution && options.resolution !== "Original") {
				const scaleMap: Record<string, string> = {
					"4K": "3840:2160",
					"1080p": "1920:1080",
					"720p": "1280:720",
					"480p": "854:480",
				};
				if (scaleMap[options.resolution]) {
					args.push("-vf", `scale=${scaleMap[options.resolution]}`);
				}
			}

			if (options.frameRate && options.frameRate !== "Original") {
				args.push("-r", options.frameRate.replace(" fps", ""));
			}

			if (options.keepAudio !== false) {
				args.push("-c:a", options.audioCodec || defaultAudioCodec);
			} else {
				args.push("-an");
			}
			break;
		}

		case "audio": {
			args.push("-i", inputName);
			args.push("-vn");

			// Map format to codec (only using codecs available in ffmpeg.wasm 0.11.0)
			const audioCodecMap: Record<string, string> = {
				mp3: "libmp3lame",
				m4a: "aac",
				aac: "aac",
				wav: "pcm_s16le",
				flac: "flac",
				ogg: "libvorbis",
				opus: "libopus",
				aiff: "pcm_s16le",
				// Note: ALAC, AMR, and WMA are not supported in ffmpeg.wasm 0.11.0
			};

			const audioFormatLower = options.format.toLowerCase();

			// Check if format is supported
			if (!audioCodecMap[audioFormatLower]) {
				throw new Error(
					`Format ${options.format} is not supported in browser-based conversion. Try MP3, M4A, AAC, WAV, FLAC, OGG, Opus, or AIFF.`,
				);
			}

			const audioCodec = audioCodecMap[audioFormatLower];
			args.push("-c:a", audioCodec);

			if (options.bitrate && options.bitrate !== "Auto") {
				args.push("-b:a", options.bitrate);
			}

			if (options.sampleRate && options.sampleRate !== "Original") {
				args.push(
					"-ar",
					options.sampleRate.replace(" kHz", "").replace("k", "000"),
				);
			}

			if (options.channels && options.channels !== "Original") {
				args.push("-ac", options.channels === "Mono" ? "1" : "2");
			}

			// For WAV/AIFF (uncompressed formats), ensure proper headers
			if (audioFormatLower === "wav" || audioFormatLower === "aiff") {
				args.push("-write_id3v2", "0");
			}
			break;
		}

		case "image": {
			// Add time trimming for GIF
			// -ss needs to be before -i for faster seeking (input seeking)
			if (options.startTime !== undefined && options.startTime > 0) {
				args.push("-ss", options.startTime.toString());
			}
			args.push("-i", inputName);
			if (options.duration !== undefined && options.duration > 0) {
				args.push("-t", options.duration.toString());
			}

			const fps = options.gifFrameRate || "15 fps";
			const size = options.gifSize || "Original";

			let filterChain = `fps=${fps.replace(" fps", "")}`;

			if (size !== "Original") {
				const scaleMap: Record<string, string> = {
					"1080p": "1920:-1",
					"720p": "1280:-1",
					"480p": "854:-1",
				};
				if (scaleMap[size]) {
					filterChain += `,scale=${scaleMap[size]}:flags=lanczos`;
				}
			}

			args.push("-vf", filterChain);

			if (options.loop === "Forever") {
				// Default for GIF
			} else if (options.loop === "Once") {
				args.push("-loop", "1");
			} else if (options.loop === "3 times") {
				args.push("-loop", "3");
			}
			break;
		}
	}

	args.push(outputName);
	return args;
}

function getMimeType(format: string): string {
	const mimeTypes: Record<string, string> = {
		MP4: "video/mp4",
		MOV: "video/quicktime",
		MKV: "video/x-matroska",
		WebM: "video/webm",
		AVI: "video/x-msvideo",
		M4V: "video/x-m4v",
		FLV: "video/x-flv",
		WMV: "video/x-ms-wmv",
		"3GP": "video/3gpp",
		OGV: "video/ogg",
		MPEG: "video/mpeg",
		TS: "video/mp2t",
		MP3: "audio/mpeg",
		M4A: "audio/mp4",
		WAV: "audio/wav",
		AAC: "audio/aac",
		FLAC: "audio/flac",
		OGG: "audio/ogg",
		Opus: "audio/opus",
		AIFF: "audio/aiff",
		GIF: "image/gif",
	};

	return mimeTypes[format] || "application/octet-stream";
}

export function getSupportedFormats(
	category: "video" | "audio" | "image",
): string[] {
	const formats: Record<"video" | "audio" | "image", string[]> = {
		video: [
			"MP4",
			"MOV",
			"MKV",
			"WebM",
			"AVI",
			"M4V",
			"FLV",
			"WMV",
			"3GP",
			"OGV",
			"MPEG",
			"TS",
		],
		audio: ["MP3", "M4A", "WAV", "AAC", "FLAC", "OGG", "Opus", "AIFF"],
		image: ["GIF"],
	};
	return formats[category];
}

export interface ResizeOptions {
	targetWidth: number;
	targetHeight: number;
	fitMode: "fit" | "fill";
	focusX: number; // 0-100 percentage
	focusY: number; // 0-100 percentage
	background: "dark" | "light" | "blue" | "blur";
	format: string;
	quality: string;
	keepAudio: boolean;
}

export interface CropOptions {
	cropX: number; // pixel position
	cropY: number; // pixel position
	cropWidth: number; // pixel width
	cropHeight: number; // pixel height
	format: string;
	quality: string;
	keepAudio: boolean;
}

export async function resizeVideo({
	file,
	options,
	onProgress,
}: {
	file: File;
	options: ResizeOptions;
	onProgress?: (progress: ConversionProgress) => void;
}): Promise<Blob> {
	const ffmpeg = ffmpegInstance;
	if (!ffmpeg) {
		throw new Error("FFmpeg not loaded. Call loadFFmpeg() first.");
	}

	const inputName = `input_${Date.now()}_${file.name}`;
	const outputName = `output_${Date.now()}.${options.format.toLowerCase()}`;

	// Set progress callback
	ffmpeg.setProgress(({ ratio }) => {
		onProgress?.({ time: 0, percent: Math.round(ratio * 100) });
	});

	// Write input file
	ffmpeg.FS("writeFile", inputName, await fileToUint8Array(file));

	// Build FFmpeg filter chain for resize
	const filterChain = buildResizeFilter(options);

	const args: string[] = [];

	// Input file
	args.push("-i", inputName);

	// Video filter
	args.push("-vf", filterChain);

	// Map to available video codecs for the format
	const formatLower = options.format.toLowerCase();
	let videoCodec = "libx264";
	let audioCodec = "aac";

	if (formatLower === "webm") {
		videoCodec = "libvpx-vp9";
		audioCodec = "libopus";
	}

	args.push("-c:v", videoCodec);

	// Quality preset
	args.push(
		"-preset",
		options.quality === "Smaller"
			? "faster"
			: options.quality === "Crisper"
				? "slower"
				: "medium",
	);

	// Audio handling
	if (options.keepAudio) {
		args.push("-c:a", audioCodec);
	} else {
		args.push("-an");
	}

	// Output file
	args.push(outputName);

	// Run FFmpeg
	await ffmpeg.run(...args);

	// Read output file
	const data = ffmpeg.FS("readFile", outputName);

	// Clean up
	ffmpeg.FS("unlink", inputName);
	ffmpeg.FS("unlink", outputName);

	return new Blob([copyToArrayBuffer(data)], {
		type: getMimeType(options.format),
	});
}

export async function cropVideo({
	file,
	options,
	onProgress,
}: {
	file: File;
	options: CropOptions;
	onProgress?: (progress: ConversionProgress) => void;
}): Promise<Blob> {
	const ffmpeg = ffmpegInstance;
	if (!ffmpeg) {
		throw new Error("FFmpeg not loaded. Call loadFFmpeg() first.");
	}

	const inputName = `input_${Date.now()}_${file.name}`;
	const outputName = `output_${Date.now()}.${options.format.toLowerCase()}`;

	// Set progress callback
	ffmpeg.setProgress(({ ratio }) => {
		onProgress?.({ time: 0, percent: Math.round(ratio * 100) });
	});

	// Write input file
	ffmpeg.FS("writeFile", inputName, await fileToUint8Array(file));

	const args: string[] = [];

	// Input file
	args.push("-i", inputName);

	// Crop filter: crop=w:h:x:y
	const cropFilter = `crop=${options.cropWidth}:${options.cropHeight}:${options.cropX}:${options.cropY}`;
	args.push("-vf", cropFilter);

	// Map to available video codecs for the format
	const formatLower = options.format.toLowerCase();
	let videoCodec = "libx264";
	let audioCodec = "aac";

	if (formatLower === "webm") {
		videoCodec = "libvpx-vp9";
		audioCodec = "libopus";
	}

	args.push("-c:v", videoCodec);

	// Quality preset
	args.push(
		"-preset",
		options.quality === "High"
			? "slower"
			: options.quality === "Smaller file"
				? "faster"
				: "medium",
	);

	// Audio handling
	if (options.keepAudio) {
		args.push("-c:a", audioCodec);
	} else {
		args.push("-an");
	}

	// Output file
	args.push(outputName);

	// Run FFmpeg
	await ffmpeg.run(...args);

	// Read output file
	const data = ffmpeg.FS("readFile", outputName);

	// Clean up
	ffmpeg.FS("unlink", inputName);
	ffmpeg.FS("unlink", outputName);

	return new Blob([copyToArrayBuffer(data)], {
		type: getMimeType(options.format),
	});
}

function buildResizeFilter(options: ResizeOptions): string {
	const { targetWidth, targetHeight, fitMode, focusX, focusY, background } =
		options;
	const filters: string[] = [];

	// First, ensure the video is scaled appropriately
	if (fitMode === "fit") {
		// Fit mode: Scale video to fit within target dimensions, then pad
		// Scale to fit within target while maintaining aspect ratio
		const scaleFilter = `scale=w=${targetWidth}:h=${targetHeight}:force_original_aspect_ratio=decrease`;
		filters.push(scaleFilter);

		// Add padding based on background choice
		const bgColor = getBackgroundColor(background);
		const padFilter = `pad=${targetWidth}:${targetHeight}:(ow-iw)/2:(oh-ih)/2:${bgColor}`;
		filters.push(padFilter);
	} else {
		// Fill mode: Scale video to cover target dimensions, then crop
		// Scale to cover the target area
		const scaleFilter = `scale=w=${targetWidth}:h=${targetHeight}:force_original_aspect_ratio=increase`;
		filters.push(scaleFilter);

		// Calculate crop position based on focus point
		// focusX and focusY are 0-100 percentages
		// For FFmpeg crop, we need to calculate the position
		// The video after scaling is larger than target in one dimension
		// We need to crop to the exact target size

		// The crop filter will crop from center by default
		// We adjust based on focus point
		// focus point affects where we crop from

		// Calculate crop x and y based on focus point
		// Since the scaled video is larger, we calculate offset
		// focusX=50 means center, focusX=0 means left (keep left), focusX=100 means right

		// Use crop with iw and oh (input width, output height)
		// The position calculation: (iw-ow)*((100-focus)/100) for x position
		// When focus is left (0), we want to keep left, so crop from right
		// When focus is right (100), we want to keep right, so crop from left

		const xExpr = `(iw-ow)*((100-${focusX})/100)`;
		const yExpr = `(ih-oh)*((100-${focusY})/100)`;

		const cropFilter = `crop=${targetWidth}:${targetHeight}:${xExpr}:${yExpr}`;
		filters.push(cropFilter);
	}

	return filters.join(",");
}

function getBackgroundColor(background: string): string {
	switch (background) {
		case "dark":
			return "0x0a0a0a"; // Dark slate
		case "light":
			return "0xf1f5f9"; // Light slate
		case "blue":
			return "0x2563eb"; // Blue
		case "blur":
			return "0x0a0a0a@0"; // Black with zero opacity (handled separately)
		default:
			return "0x000000";
	}
}

export interface RotateOptions {
	rotation: 0 | 90 | 180 | 270;
	flipHorizontal: boolean;
	flipVertical: boolean;
	format: string;
	quality: string;
	keepAudio: boolean;
}

export async function rotateVideo({
	file,
	options,
	onProgress,
}: {
	file: File;
	options: RotateOptions;
	onProgress?: (progress: ConversionProgress) => void;
}): Promise<Blob> {
	const ffmpeg = ffmpegInstance;
	if (!ffmpeg) {
		throw new Error("FFmpeg not loaded. Call loadFFmpeg() first.");
	}

	const inputName = `input_${Date.now()}_${file.name}`;
	const outputName = `output_${Date.now()}.${options.format.toLowerCase()}`;

	// Set progress callback
	ffmpeg.setProgress(({ ratio }) => {
		onProgress?.({ time: 0, percent: Math.round(ratio * 100) });
	});

	// Write input file
	ffmpeg.FS("writeFile", inputName, await fileToUint8Array(file));

	const args: string[] = [];

	// Input file
	args.push("-i", inputName);

	// Build transpose filter for rotation
	// transpose=0: 90° counterclockwise (270° clockwise)
	// transpose=1: 90° clockwise
	// transpose=2: 90° counterclockwise with vertical flip
	// transpose=3: 90° clockwise with vertical flip
	const filters: string[] = [];

	if (options.rotation === 90) {
		filters.push("transpose=1");
	} else if (options.rotation === 180) {
		// 180° rotation: flip both horizontally and vertically
		filters.push("transpose=1,transpose=1");
	} else if (options.rotation === 270) {
		filters.push("transpose=0");
	}

	// Add flip filters if needed
	if (options.flipHorizontal) {
		filters.push("hflip");
	}
	if (options.flipVertical) {
		filters.push("vflip");
	}

	// Apply the filter chain if any filters exist
	if (filters.length > 0) {
		args.push("-vf", filters.join(","));
	}

	// Map to available video codecs for the format
	const formatLower = options.format.toLowerCase();
	let videoCodec = "libx264";
	let audioCodec = "aac";

	if (formatLower === "webm") {
		videoCodec = "libvpx-vp9";
		audioCodec = "libopus";
	}

	args.push("-c:v", videoCodec);

	// Quality preset
	args.push(
		"-preset",
		options.quality === "High"
			? "slower"
			: options.quality === "Smaller file"
				? "faster"
				: "medium",
	);

	// Audio handling
	if (options.keepAudio) {
		args.push("-c:a", audioCodec);
	} else {
		args.push("-an");
	}

	// Output file
	args.push(outputName);

	// Run FFmpeg
	await ffmpeg.run(...args);

	// Read output file
	const data = ffmpeg.FS("readFile", outputName);

	// Clean up
	ffmpeg.FS("unlink", inputName);
	ffmpeg.FS("unlink", outputName);

	return new Blob([copyToArrayBuffer(data)], {
		type: getMimeType(options.format),
	});
}
